// CORE-847 §5 — «добавить пользователю возможность перетаскивать виджет, если он
// мешает контенту».
//
// Built on RN's PanResponder rather than DOM pointer events: react-native-web
// backs it with the same mouse + touch responder system, so this file stays free
// of `window`/`document` (I-2) and carries over to the native pass unchanged.
//
// The launcher's own Pressable keeps the click. The responder only takes over
// once the pointer has travelled past DRAG_THRESHOLD_PX — that is the whole of
// «короткий click/tap не должен ошибочно определяться как drag»: below the
// threshold the child never loses the gesture, above it the capture handler
// pulls it away and the press is cancelled instead of firing on release.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PanResponder, Platform, useWindowDimensions, type GestureResponderHandlers } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { storage } from '../utils/storage';
import { useStableHeight } from './useStableHeight';
import { useReservedZones } from './reservedZones';
import { useInteractiveOverlap } from './useInteractiveOverlap';
import {
  AVATAR_SIZE,
  NATIVE_EDGE_MARGIN,
  WIDGET_SAFE_MARGIN,
  avoidReservedZones,
  clampAnchor,
  exceedsDragThreshold,
  isMobileWebLayout,
  labelSide,
  nearestDock,
  placeAnchor,
  readStoredAnchor,
  snapAnchor,
  type Insets,
  type Point,
  type Size,
} from './widgetPosition';

const IS_WEB = Platform.OS === 'web';

/** «Просто 15px от краёв экрана» on a phone; the web keeps its 24. Platform is
 *  fixed for the process lifetime, so this is a module constant, not state. */
const MARGIN = IS_WEB ? WIDGET_SAFE_MARGIN : NATIVE_EDGE_MARGIN;

/** Per-browser, not per-account: where the widget sits is a property of the
 *  screen it is in the way on, and the next person on this device inherits a
 *  sane corner, not someone else's parking spot on their own machine. */
const ANCHOR_KEY = 'agape_agapella_widget_anchor_v1';

/**
 * How long after a drag a press is ignored.
 *
 * Losing the responder cancels RN's own press, but on web the browser still
 * fires its native `click` on mouse-up — so a drag that ended over the avatar
 * (which it always does; the avatar is what you dragged) opened the chat every
 * single time. Verified in a browser: drag the launcher across the page and the
 * pop-up came up on release. Nothing a human does gets a real click in under
 * this window, and the stray one arrives in the same tick.
 */
const PRESS_SUPPRESS_MS = 350;

export interface WidgetDrag {
  /** Top-left of the avatar, always inside the safe area. */
  anchor: Point;
  /** Which way the hover caption unfolds from under the avatar. */
  side: 'left' | 'right';
  dragging: boolean;
  /** False until the stored position has been read — see the widget's fade-in. */
  ready: boolean;
  viewport: Size;
  insets: Insets;
  panHandlers: GestureResponderHandlers;
  /** True when this press is the tail of a drag and must not toggle the chat. */
  wasJustDragged: () => boolean;
}

/**
 * @param reservedBottom CORE-731 — extra clearance to keep along the bottom edge
 *   on routes whose own UI owns it (Live page donate/composer, green-room CTA +
 *   audio meter). Stacked onto the device bottom inset so both the default
 *   position and any dragged one stay above those controls instead of hiding the
 *   launcher there. 0 on ordinary screens leaves the corner behaviour untouched.
 * @param routeKey CORE-1120 — маршрут, на котором сейчас находимся. Сам по себе
 *   он ничего не решает: это повод перемерить, что лежит под иконкой, когда
 *   страница сменилась целиком.
 * @param dockOnDrop Owner's call (25.08.2026): while the chat window is open the
 *   drop lands on one of the six docks ({@link nearestDock}) instead of wherever
 *   the pointer was. Free placement is what the bare icon keeps — the constraint
 *   exists because an open window has to sit somewhere legible, not because
 *   parking the icon needs correcting.
 */
export function useWidgetDrag(
  size: number = AVATAR_SIZE,
  reservedBottom = 0,
  dockOnDrop = false,
  routeKey?: string,
): WidgetDrag {
  const { width, height: liveHeight } = useWindowDimensions();
  // Address-bar churn (see useStableHeight's note) is a HEIGHT-only effect —
  // a genuine resize/rotation changes width too, which is what re-baselines it.
  const height = useStableHeight(width, liveHeight);
  const safeArea = useSafeAreaInsets();
  // CORE-1120 — прямоугольники, объявленные занятыми теми экранами, что сейчас
  // на виду: заголовки, CTA, поле ввода, баннеры. Пустой список на экране,
  // который ничего не объявил, — и тогда геометрия ровно та же, что была.
  const zones = useReservedZones();
  // Owner, 12.10.2026 — «сделай как на нативке»: a phone-width web layout keeps
  // the native rules below (corner on drop, no hit-testing relocation). Only the
  // declared zones — header, Live bottom band — still move it, as on native.
  const mobileWeb = isMobileWebLayout(IS_WEB, width);

  const viewport = useMemo<Size>(() => ({ width, height }), [width, height]);
  const insets = useMemo<Insets>(
    () => ({ top: safeArea.top, right: safeArea.right, bottom: safeArea.bottom + reservedBottom, left: safeArea.left }),
    [safeArea.top, safeArea.right, safeArea.bottom, safeArea.left, reservedBottom],
  );
  // CORE-1376 — лежать на нижней панели вкладок иконке больше нельзя: она
  // забирала нажатие правой вкладки себе, и «Профиль» не открывался. Угол за
  // ней остаётся, полосу самой панели передаёт `reservedBottom` — её измеряет
  // и объявляет BottomTabBar.native (см. AgapellaWidget.native).

  // `null` means «пользователь не перетаскивал» — and then the position is
  // DERIVED from the viewport rather than remembered. Storing the default as a
  // point would tear the launcher off the corner the moment the window is
  // resized, which reads as a bug even though every pixel is still legal.
  const [custom, setCustom] = useState<Point | null>(null);
  const [dragging, setDragging] = useState(false);
  const [ready, setReady] = useState(false);

  // Web parks wherever the pointer dropped it; native snaps the stored point to
  // its quadrant's corner — DERIVED at render, not baked into storage, so a
  // rotation or a different device re-computes the same corner rather than
  // clamping a stale coordinate. Mid-drag the icon follows the finger freely.
  // CORE-1120 — всё правило целиком в {@link resolveAnchor}: края, углы натива
  // и обход занятых страницей областей. Пересчёт при смене маршрута, размера
  // окна и поворота происходит сам собой — позиция ВЫВОДИТСЯ из аргументов на
  // каждом рендере, а не хранится готовой.
  //
  // Во время перетаскивания зоны не применяются: иконка обязана идти за
  // пальцем, иначе она вырывается из-под курсора. Место разрешается на
  // отпускании — и на любом следующем рендере.
  // База — только края, safe-area и углы натива. Её же щупает проверка живых
  // перекрытий ниже: спрашивать про итоговую позицию значило бы зациклиться.
  const base = useMemo(() => placeAnchor({
    stored: custom,
    viewport,
    insets,
    size,
    margin: MARGIN,
    snap: (!IS_WEB || mobileWeb) && !dragging,
  }), [custom, dragging, mobileWeb, viewport, insets, size]);

  const anchor = useMemo(
    () => (dragging
      ? base
      : avoidReservedZones(base, zones, viewport, insets, size, MARGIN)),
    [base, dragging, viewport, insets, size, zones],
  );

  // CORE-1120, QA FAIL на мобильном: в углу по умолчанию иконка садилась на
  // «Все мастера» и «Сначала новые» — обычные элементы страницы, которых не
  // объявлял никто и объявить не мог. Спрашиваем у браузера, что под кружком,
  // и найденное попадает в тот же реестр, что и шапка.
  useInteractiveOverlap({
    base,
    viewport,
    // Те же края, что у геометрии выше: круг замера сам считает, куда иконка
    // уедет с занятого места, и обязан считать это её правилами, а не своими.
    insets,
    margin: MARGIN,
    size,
    routeKey,
    // Во время перетаскивания иконка идёт за пальцем, а с открытым окном она
    // уже не одна на экране — двигать её под курсором нельзя.
    // Mobile web: off — the icon stays in its corner over whatever is there,
    // exactly like the native launcher (owner, 12.10.2026); drag moves it.
    enabled: !dragging && !dockOnDrop && !mobileWeb,
  });

  // PanResponder is built once; its callbacks close over the first render, so
  // everything they read lives in a ref.
  const anchorRef = useRef(anchor);
  const viewportRef = useRef(viewport);
  const insetsRef = useRef(insets);
  const sizeRef = useRef(size);
  /** Anchor at the moment the drag was granted. */
  const startRef = useRef<Point>(anchor);
  /** Travel already spent reaching the threshold — subtracted so the avatar
   *  doesn't jump those 8 px the instant the drag is recognised. */
  const grantRef = useRef<Point>({ x: 0, y: 0 });
  const dragEndedAtRef = useRef(0);
  const dockRef = useRef(dockOnDrop);
  const mobileWebRef = useRef(mobileWeb);

  useEffect(() => { anchorRef.current = anchor; }, [anchor]);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);
  useEffect(() => { insetsRef.current = insets; }, [insets]);
  useEffect(() => { sizeRef.current = size; }, [size]);
  useEffect(() => { dockRef.current = dockOnDrop; }, [dockOnDrop]);
  useEffect(() => { mobileWebRef.current = mobileWeb; }, [mobileWeb]);

  useEffect(() => {
    let mounted = true;
    storage
      .getJSON<Point>(ANCHOR_KEY)
      .then((raw) => {
        if (!mounted) return;
        const stored = readStoredAnchor(raw);
        if (stored) setCustom(stored);
        setReady(true);
      })
      .catch(() => { if (mounted) setReady(true); });
    return () => { mounted = false; };
  }, []);

  const panHandlers = useMemo(() => {
    const finish = () => {
      setDragging(false);
      dragEndedAtRef.current = Date.now();
      // Native: resolve the corner NOW and make it the stored state, not a
      // render-time derivation. When `custom` kept the raw drop point and the
      // corner was derived per render, the next drag's first frame flipped the
      // derivation back to raw (`dragging` switches the branch) and the icon
      // teleported to last drop's coordinates for a frame — the reported
      // «дёргается, блики» at drag start.
      const dropped = anchorRef.current;
      // Native always takes its quadrant's corner; web parks where it was let
      // go, unless the chat window is open — then it takes the nearest dock.
      // Native and phone-width web take the quadrant's corner; desktop web parks
      // where it was let go, unless the chat window is open — then the nearest dock.
      const resolve = (!IS_WEB || mobileWebRef.current) ? snapAnchor : dockRef.current ? nearestDock : null;
      const parked = resolve
        ? resolve(dropped, viewportRef.current, insetsRef.current, sizeRef.current, MARGIN)
        : dropped;
      // A resolved corner or dock is a different point from the one the pointer
      // left, so it has to become state — and the launcher glides to it. Plain
      // web free-drag needs no write-back: `custom` already holds that drop from
      // the last move.
      if (resolve) setCustom(parked);
      // One write per drag, on release — not per pointer move.
      void storage.setJSON(ANCHOR_KEY, parked);
    };

    return PanResponder.create({
      // A press belongs to the child until it turns into travel.
      onStartShouldSetPanResponder: () => false,
      onStartShouldSetPanResponderCapture: () => false,
      onMoveShouldSetPanResponder: (_e, g) => exceedsDragThreshold(g.dx, g.dy),
      onMoveShouldSetPanResponderCapture: (_e, g) => exceedsDragThreshold(g.dx, g.dy),
      onPanResponderGrant: (_e, g) => {
        startRef.current = anchorRef.current;
        grantRef.current = { x: g.dx, y: g.dy };
        setDragging(true);
      },
      onPanResponderMove: (_e, g) => {
        const start = startRef.current;
        const spent = grantRef.current;
        setCustom(
          clampAnchor(
            { x: start.x + g.dx - spent.x, y: start.y + g.dy - spent.y },
            viewportRef.current,
            insetsRef.current,
            sizeRef.current,
            MARGIN,
          ),
        );
      },
      onPanResponderRelease: finish,
      onPanResponderTerminate: finish,
      onPanResponderTerminationRequest: () => false,
    }).panHandlers;
  }, []);

  const wasJustDragged = useCallback(
    () => Date.now() - dragEndedAtRef.current < PRESS_SUPPRESS_MS,
    [],
  );

  return {
    anchor,
    side: labelSide(anchor.x, viewport, size),
    dragging,
    ready,
    viewport,
    insets,
    panHandlers,
    wasJustDragged,
  };
}
